import React, { Component } from 'react'
import {
  ActionButton,
  ActionGroup,
  Button,
  ButtonGroup,
  Cell,
  Checkbox,
  Column,
  Content as DialogContent,
  Dialog,
  DialogTrigger,
  Divider,
  Flex,
  Heading,
  Item,
  Menu,
  MenuTrigger,
  NumberField,
  Picker,
  ProgressCircle,
  Provider,
  Row,
  TabList,
  TabPanels,
  Tabs,
  TableBody,
  TableHeader,
  TableView,
  Text,
  TextField,
  View,
  Well
} from '@adobe/react-spectrum'
import { Lock, MoreVertical, Unlock } from 'lucide-react'
import { customSpectrumTheme, SPECTRUM_THEME_CLASS } from '../../themes/spectrum-theme'
import styles from './styles'
import { formatCurrency } from '../../common/utils'
import Content from '../ui/content'

// Fallback reference data — used only if backend hasn't provided them via appSettingReducer
const DEFAULT_DEVICE_CATEGORIES = [
  { key: 'LanTEK_IV', displayName: 'LanTEK IV' },
  { key: 'SignalTEK', displayName: 'SignalTEK 10G & SignalTEK QT & Pro' },
  { key: 'VDV_PoE', displayName: 'VDV II Range & PoE PRO' }
]

const DEFAULT_SUPPORTED_CURRENCIES = [
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' }
]

const DEFAULT_FEATURE_DEFINITIONS = [
  { featureName: 'automated_archiving', displayName: 'Automated Archiving', category: 'Data Management' },
  { featureName: 'geolocation_device_lock', displayName: 'Geolocation of Device & Lock/Unlock', category: 'Device Management' },
  { featureName: 'organisation_metrics', displayName: 'Analysis of Organisation and Job Metrics', category: 'Analytics' },
  { featureName: 'qr_code_label', displayName: 'Generate QR Code Label', category: 'Labeling' },
  { featureName: 'smart_flag', displayName: 'Smart Flag (Suspicious Results Filter)', category: 'Quality Control' },
  { featureName: 'poe_derating', displayName: 'PoE Derating via AnyWARE', category: 'Network Analysis' },
  { featureName: 'fiber_loss_calculator', displayName: 'Fiber Loss Calculator', category: 'Network Analysis' },
  { featureName: 'link_budget_calculator', displayName: 'Link Budget Calculator', category: 'Network Analysis' },
  { featureName: 'multimode_bandwidth_calculator', displayName: 'Multimode Bandwidth Calculator', category: 'Network Analysis' }
]

const NON_SUBSCRIPTION_FEATURES = ['cloud_subscription_signup', 'lrp_free_features']

/** Resolve constants — prefer props from Redux (backend), fall back to local defaults */
const getDeviceCategories = (props) => (props.deviceCategories && props.deviceCategories.length > 0) ? props.deviceCategories : DEFAULT_DEVICE_CATEGORIES
const getSupportedCurrencies = (props) => (props.supportedCurrencies && props.supportedCurrencies.length > 0) ? props.supportedCurrencies : DEFAULT_SUPPORTED_CURRENCIES
const getFeatureDefinitions = (props) => {
  const definitions = (props.featureDefinitions && props.featureDefinitions.length > 0) ? props.featureDefinitions : DEFAULT_FEATURE_DEFINITIONS
  return definitions.filter(def => !NON_SUBSCRIPTION_FEATURES.includes(def.featureName))
}

/** Format smallest-unit price to display string: 28000 → "£280.00" */
const formatPrice = (amount, currencyCode) => {
  return formatCurrency(amount, currencyCode, { minorUnits: true })
}

/** Build a complete pricing grid: every device category × every currency, pre-filled from existing data */
const buildPricingGrid = (existingPricing, deviceCategories, supportedCurrencies) => {
  const grid = []
  deviceCategories.forEach(dc => {
    supportedCurrencies.forEach(curr => {
      const existing = (existingPricing || []).find(
        p => (p.deviceCategory === dc.key || p.deviceCategory === dc.displayName) && p.currency === curr.code
      )
      grid.push({
        deviceCategory: dc.key,
        deviceCategoryDisplay: dc.displayName,
        currency: curr.code,
        currencySymbol: curr.symbol,
        price1Device: existing ? existing.price1Device : 0,
        price2To5: existing ? existing.price2To5 : 0,
        price6Plus: existing ? existing.price6Plus : 0
      })
    })
  })
  return grid
}

/** Extract Terms & Conditions version ID (camelCase from API, with legacy fallback) */
const getTermsId = (terms) => terms && (terms.id || terms.Id)

class SubscriptionManagement extends Component<any, any> {
  _editorIframe: any

  constructor(props) {
    super(props)
    this.state = {
      // Tier form
      showTierForm: false,
      editingTier: null,
      tierForm: { tierKey: '', name: '', description: '', isActive: true, sortOrder: 0, stripeProductName: '' },
      tierFormErrors: {},
      // Features modal
      featuresModalTier: null,
      featureForm: [],
      // Pricing modal
      pricingModalTier: null,
      pricingForm: [],
      // Country form
      showCountryForm: false,
      editingCountry: null,
      countryForm: { countryCode: '', countryName: '', isActive: true, sortOrder: 0, stripeSellerKey: '' },
      // Currency form
      editingCountryCurrencies: null,
      currencyForm: [],
      // Terms & Conditions form
      showTermsForm: false,
      editingTerms: null,
      termsForm: { languageCode: 'en-GB', version: '', title: '', content: '' },
      termsFormErrors: {},
      termsContentLoading: false,
      showHtmlSource: false,
      // Active main tab (tiers | countries | terms)
      mainTab: 'tiers',
      // Pricing protection
      pricingLocked: true,
      pricingOriginal: [],
      showPricingConfirm: false,
      pricingConfirmTierName: ''
    }
  }

  // ── Tier CRUD ──

  handleNewTier = () => {
    this.setState({
      showTierForm: true,
      editingTier: null,
      tierForm: { tierKey: '', name: '', description: '', isActive: true, sortOrder: 0, stripeProductName: '' },
      tierFormErrors: {}
    })
  }

  handleEditTier = (tier) => {
    this.setState({
      showTierForm: true,
      editingTier: tier,
      tierForm: {
        tierKey: tier.tierKey || '',
        name: tier.name || '',
        description: tier.description || '',
        isActive: tier.isActive !== false,
        sortOrder: tier.sortOrder || 0,
        stripeProductName: tier.stripeProductName || ''
      },
      tierFormErrors: {}
    })
  }

  validateTierForm = () => {
    const { tierForm, editingTier } = this.state
    const { translate } = this.props
    const errors: any = {}
    if (!editingTier && !tierForm.tierKey.trim()) errors.tierKey = translate('tier_key_required')
    if (!tierForm.name.trim()) errors.name = translate('name_required')
    if (!tierForm.stripeProductName.trim()) errors.stripeProductName = translate('stripe_product_name_required')
    this.setState({ tierFormErrors: errors })
    return Object.keys(errors).length === 0
  }

  handleSaveTier = () => {
    if (!this.validateTierForm()) return
    const { editingTier, tierForm } = this.state
    if (editingTier) {
      this.props.onUpdateTier(editingTier.id, {
        name: tierForm.name,
        description: tierForm.description,
        isActive: tierForm.isActive,
        sortOrder: tierForm.sortOrder,
        stripeProductName: tierForm.stripeProductName
      })
    } else {
      this.props.onCreateTier(tierForm)
    }
    this.setState({ showTierForm: false, editingTier: null })
  }

  handleDeleteTier = (tierId) => {
    if (window.confirm(this.props.translate('confirm_delete_tier'))) {
      this.props.onDeleteTier(tierId)
    }
  }

  handleTierFormChange = (field, value) => {
    this.setState(prev => ({
      tierForm: { ...prev.tierForm, [field]: value },
      tierFormErrors: { ...prev.tierFormErrors, [field]: undefined }
    }))
  }

  // ── Features Modal ──

  handleOpenFeatures = (tier) => {
    this.props.onGetTierFeatures(tier.id)
    this.setState({ featuresModalTier: tier })
  }

  handleCloseFeatures = () => {
    this.setState({ featuresModalTier: null, featureForm: [] })
  }

  handleFeatureToggle = (featureName, checked) => {
    this.setState(prev => ({
      featureForm: prev.featureForm.map(f =>
        f.featureName === featureName ? { ...f, isEnabled: checked } : f
      )
    }))
  }

  handleSaveFeatures = () => {
    const { featuresModalTier, featureForm } = this.state
    const payload = featureForm.map(f => ({ featureName: f.featureName, isEnabled: f.isEnabled }))
    this.props.onUpdateTierFeatures(featuresModalTier.id, payload)
      .then(() => this.setState({ featuresModalTier: null }))
      .catch(() => {})
  }

  // ── Pricing Modal ──

  handleOpenPricing = (tier) => {
    this.props.onGetTierPricing(tier.id)
    this.setState({ pricingModalTier: tier })
  }

  handleClosePricing = () => {
    this.setState({ pricingModalTier: null, pricingForm: [], pricingLocked: true, pricingOriginal: [], showPricingConfirm: false, pricingConfirmTierName: '' })
  }

  handlePricingChange = (index, field, value) => {
    this.setState(prev => {
      const pricing = [...prev.pricingForm]
      pricing[index] = { ...pricing[index], [field]: parseInt(value) || 0 }
      return { pricingForm: pricing }
    })
  }

  /** Compute price changes between original and current form */
  getPricingDiff = () => {
    const { pricingForm, pricingOriginal } = this.state
    const changes: any[] = []
    pricingForm.forEach((p, i) => {
      const orig = pricingOriginal[i]
      if (!orig) return
      const fields = ['price1Device', 'price2To5', 'price6Plus'] as const
      fields.forEach(field => {
        if (p[field] !== orig[field]) {
          changes.push({
            deviceCategory: p.deviceCategoryDisplay,
            currency: p.currency,
            currencySymbol: p.currencySymbol,
            field,
            oldValue: orig[field],
            newValue: p[field]
          })
        }
      })
    })
    return changes
  }

  handleSavePricing = () => {
    // If there are changes, show confirmation dialog instead of saving directly
    const changes = this.getPricingDiff()
    if (changes.length === 0) {
      // No changes — just close
      this.handleClosePricing()
      return
    }
    this.setState({ showPricingConfirm: true, pricingConfirmTierName: '' })
  }

  handleConfirmSavePricing = () => {
    const { pricingModalTier, pricingForm } = this.state
    const payload = pricingForm.map(p => ({
      deviceCategory: p.deviceCategory,
      currency: p.currency,
      price1Device: p.price1Device,
      price2To5: p.price2To5,
      price6Plus: p.price6Plus
    }))
    this.props.onUpdateTierPricing(pricingModalTier.id, payload)
    this.setState({ pricingModalTier: null, pricingLocked: true, pricingOriginal: [], showPricingConfirm: false, pricingConfirmTierName: '' })
  }

  // ── Countries ──

  handleNewCountry = () => {
    this.setState({
      showCountryForm: true,
      editingCountry: null,
      countryForm: { countryCode: '', countryName: '', isActive: true, sortOrder: 0, stripeSellerKey: '' }
    })
  }

  handleEditCountry = (country) => {
    this.setState({
      showCountryForm: true,
      editingCountry: country,
      countryForm: {
        countryCode: country.countryCode || '',
        countryName: country.countryName || '',
        isActive: country.isActive !== false,
        sortOrder: country.sortOrder || 0,
        stripeSellerKey: country.stripeSellerKey || ''
      }
    })
  }

  handleSaveCountry = () => {
    const { editingCountry, countryForm } = this.state
    if (editingCountry) {
      this.props.onUpdateCountry(editingCountry.id, countryForm)
    } else {
      this.props.onCreateCountry(countryForm)
    }
    this.setState({ showCountryForm: false, editingCountry: null })
  }

  handleDeleteCountry = (countryId) => {
    if (window.confirm(this.props.translate('confirm_delete_country'))) {
      this.props.onDeleteCountry(countryId)
    }
  }

  handleCountryFormChange = (field, value) => {
    this.setState(prev => ({ countryForm: { ...prev.countryForm, [field]: value } }))
  }

  // ── Country Currencies ──

  handleEditCurrencies = (country) => {
    this.setState({
      editingCountryCurrencies: country,
      currencyForm: (country.currencies || []).map(c => ({
        currencyCode: c.currencyCode || '',
        currencySymbol: c.currencySymbol || '',
        currencyName: c.currencyName || '',
        isDefault: c.isDefault || false,
        isActive: c.isActive !== false
      }))
    })
  }

  handleCurrencyChange = (index, field, value) => {
    this.setState(prev => {
      const currencies = [...prev.currencyForm]
      if (field === 'isDefault' && value === true) {
        currencies.forEach((c, i) => { c.isDefault = i === index })
      } else if (field === 'currencyCode') {
        const known = getSupportedCurrencies(this.props).find(sc => sc.code === value)
        currencies[index] = {
          ...currencies[index],
          currencyCode: value,
          currencySymbol: known ? known.symbol : currencies[index].currencySymbol,
          currencyName: known ? known.name : currencies[index].currencyName
        }
      } else {
        currencies[index] = { ...currencies[index], [field]: value }
      }
      return { currencyForm: currencies }
    })
  }

  handleAddCurrency = () => {
    this.setState(prev => ({
      currencyForm: [...prev.currencyForm, { currencyCode: '', currencySymbol: '', currencyName: '', isDefault: false, isActive: true }]
    }))
  }

  handleRemoveCurrency = (index) => {
    this.setState(prev => ({
      currencyForm: prev.currencyForm.filter((_, i) => i !== index)
    }))
  }

  handleSaveCurrencies = () => {
    const { editingCountryCurrencies, currencyForm } = this.state
    this.props.onUpdateCurrencies(editingCountryCurrencies.id, currencyForm)
    this.setState({ editingCountryCurrencies: null })
  }

  // ── Terms & Conditions CRUD ──

  handleNewTerms = () => {
    this.setState({
      showTermsForm: true,
      editingTerms: null,
      termsForm: { languageCode: 'en-GB', version: '', title: '', content: '' },
      termsFormErrors: {},
      termsContentLoading: false,
      showHtmlSource: false
    })
  }

  handleEditTerms = (terms) => {
    // The list endpoint does not include content — fetch the full record by ID via Redux
    this.setState({
      showTermsForm: true,
      editingTerms: terms,
      termsForm: {
        languageCode: terms.languageCode || 'en-GB',
        version: terms.version || '',
        title: terms.title || '',
        content: '' // will be updated by Redux
      },
      termsFormErrors: {},
      termsContentLoading: true
    })

    this.props.onGetTermsVersion(getTermsId(terms))
  }

  handleTermsFormChange = (field, value) => {
    this.setState(prev => ({
      termsForm: { ...prev.termsForm, [field]: value },
      termsFormErrors: { ...prev.termsFormErrors, [field]: undefined }
    }))
  }

  validateTermsForm = () => {
    const { termsForm } = this.state
    const { translate } = this.props
    const errors: any = {}
    if (!termsForm.languageCode.trim()) errors.languageCode = translate('validation_language_required')
    if (!termsForm.version.trim()) errors.version = translate('validation_version_required')
    if (!termsForm.title.trim()) errors.title = translate('validation_title_required')
    if (!termsForm.content.trim()) errors.content = translate('validation_content_required')
    this.setState({ termsFormErrors: errors })
    return Object.keys(errors).length === 0
  }

  handleSaveTerms = () => {
    if (!this.validateTermsForm()) return
    const { editingTerms, termsForm } = this.state
    if (editingTerms) {
      this.props.onUpdateTermsVersion(getTermsId(editingTerms), {
        version: termsForm.version,
        title: termsForm.title,
        content: termsForm.content
      })
    } else {
      this.props.onCreateTermsVersion(termsForm)
    }
    this.setState({ showTermsForm: false, editingTerms: null })
  }

  handlePublishTerms = (termsId) => {
    if (window.confirm(this.props.translate('confirm_publish_terms'))) {
      this.props.onPublishTermsVersion(termsId)
    }
  }

  handleDeleteTerms = (termsId) => {
    if (window.confirm(this.props.translate('confirm_delete_terms'))) {
      this.props.onDeleteTermsVersion(termsId)
    }
  }

  handleDuplicateTerms = (terms) => {
    if (window.confirm(this.props.translate('confirm_duplicate_terms'))) {
      this.props.onDuplicateTermsVersion(terms.id)
    }
  }

  handlePreviewTerms = (terms) => {
    const openPreview = (termsData) => {
      const preview = window.open('', '_blank')
      if (preview) {
        preview.document.write(`
          <!DOCTYPE html>
          <html lang="${termsData.languageCode || 'en'}">
          <head>
            <meta charset="UTF-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <title>${termsData.title || 'Terms and Conditions'} — Preview</title>
            <style>
              body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; max-width: 800px; margin: 0 auto; padding: 40px 24px; color: #333; line-height: 1.6; }
              h1 { color: #005795; border-bottom: 2px solid #005795; padding-bottom: 12px; }
              .meta { color: #666; font-size: 13px; margin-bottom: 24px; }
              .preview-badge { display: inline-block; background: #fff3cd; color: #856404; padding: 4px 12px; border-radius: 4px; font-size: 12px; font-weight: 600; margin-bottom: 16px; }
            </style>
          </head>
          <body>
            <span class="preview-badge">PREVIEW — ${termsData.status}</span>
            <h1>${termsData.title || 'Terms and Conditions'}</h1>
            <div class="meta">Version: ${termsData.version} | Language: ${termsData.languageCode}</div>
            <div>${termsData.content || '<p>No content</p>'}</div>
          </body>
          </html>
        `)
        preview.document.close()
      }
    }

    // If content is available (e.g. from the form), preview directly
    if (terms.content) {
      openPreview(terms)
    } else if (getTermsId(terms)) {
      // Fetch full content by ID via Redux for list items (which don't include content)
      this.props.onGetTermsVersion(getTermsId(terms))
      // Since preview in new tab usually needs to happen on user interaction,
      // and we just started a Redux fetch, we'll have to wait for the update.
      // However, the original code used a direct fetch and then openPreview.
      // To keep it simple and correct with Redux, we'll suggest using the preview once data arrives
      // or using a small local fetch if absolutely needed for immediate window.open (browsers block delayed window.open).
      // But the requirement is to go through Redux store.
      // Let's assume for now the user can click preview again or we can try to handle it in componentDidUpdate.
      alert(this.props.translate('terms_fetching_preview'))
    } else {
      openPreview(terms)
    }
  }

  // ── Sync props → local state ──

  componentDidUpdate(prevProps) {
    if (prevProps.tierFeatures !== this.props.tierFeatures) {
      const saved = this.props.tierFeatures || []
      const savedMap = {}
      saved.forEach(f => { savedMap[f.featureName] = f })
      this.setState({
        featureForm: getFeatureDefinitions(this.props).map(def => ({
          featureName: def.featureName,
          displayName: def.displayName,
          category: def.category,
          isEnabled: savedMap[def.featureName] ? savedMap[def.featureName].isEnabled : false
        }))
      })
    }
    if (prevProps.tierPricing !== this.props.tierPricing) {
      const grid = buildPricingGrid(this.props.tierPricing, getDeviceCategories(this.props), getSupportedCurrencies(this.props))
      this.setState({
        pricingForm: grid,
        pricingOriginal: grid.map(p => ({ ...p })),
        pricingLocked: true,
        showPricingConfirm: false,
        pricingConfirmTierName: ''
      })
    }
    if (prevProps.termsVersions !== this.props.termsVersions && this.state.editingTerms) {
      const updated = (this.props.termsVersions || []).find(t => getTermsId(t) === getTermsId(this.state.editingTerms))
      if (updated && updated.content && updated.content !== this.state.termsForm.content) {
        this.setState(prev => ({
          termsForm: { ...prev.termsForm, content: updated.content },
          termsContentLoading: false
        }), () => {
          this.syncEditorContent()
        })
      } else if (updated && !this.props.loading && this.state.termsContentLoading) {
        this.setState({ termsContentLoading: false })
      }
    }
    if (this.props.successMessage && !prevProps.successMessage) {
      setTimeout(() => this.props.onClearMessages(), 3000)
    }
  }

  // ── Render: Messages ──

  renderMessages() {
    const { error, errorMessage, successMessage, saving, translate } = this.props
    return (
      <View marginBottom="size-200">
        {saving && (
          <Well UNSAFE_style={{ backgroundColor: '#e3f2fd', borderColor: '#90caf9', color: '#1565c0' }}>
            <Flex alignItems="center" gap="size-100">
              <ProgressCircle aria-label="Saving" size="S" isIndeterminate />
              <Text>{translate('saving')}</Text>
            </Flex>
          </Well>
        )}
        {successMessage && (
          <Well UNSAFE_style={{ backgroundColor: '#e8f5e9', borderColor: '#a5d6a7', color: '#2e7d32' }}>
            <Text>{successMessage}</Text>
          </Well>
        )}
        {error && errorMessage && (
          <Well UNSAFE_style={{ backgroundColor: '#ffebee', borderColor: '#ef9a9a', color: '#c62828' }}>
            <Text>{errorMessage}</Text>
          </Well>
        )}
      </View>
    )
  }

  // ── Render: Tiers (main view) ──

  renderTiersView() {
    const { tiers, loading, translate } = this.props
    const { showTierForm, editingTier, tierForm, tierFormErrors } = this.state

    return (
      <View>
        <Flex justifyContent="space-between" alignItems="center" marginBottom="size-200">
          <Heading level={3} margin="size-0">{translate('subscription_tiers')}</Heading>
          <Button variant="cta" onPress={this.handleNewTier}>{translate('new_tier')}</Button>
        </Flex>

        {showTierForm && (
          <Well marginBottom="size-200">
            <Heading level={4} marginBottom="size-100">{editingTier ? translate('edit_tier') : translate('new_tier_label')}</Heading>
            <Flex direction="column" gap="size-150" wrap>
              <Flex gap="size-200" wrap>
                {!editingTier && (
                  <TextField
                    label={`${translate('tier_key_label')} *`}
                    value={tierForm.tierKey}
                    onChange={val => this.handleTierFormChange('tierKey', val)}
                    placeholder="e.g. plus"
                    validationState={tierFormErrors.tierKey ? 'invalid' : undefined}
                    errorMessage={tierFormErrors.tierKey}
                    width="size-2400"
                  />
                )}
                <TextField
                  label={`${translate('name')} *`}
                  value={tierForm.name}
                  onChange={val => this.handleTierFormChange('name', val)}
                  placeholder="e.g. Plus"
                  validationState={tierFormErrors.name ? 'invalid' : undefined}
                  errorMessage={tierFormErrors.name}
                  width="size-2400"
                />
                <TextField
                  label={translate('description')}
                  value={tierForm.description}
                  onChange={val => this.handleTierFormChange('description', val)}
                  width="size-2400"
                />
                <NumberField
                  label={translate('sort_order')}
                  value={tierForm.sortOrder}
                  onChange={val => this.handleTierFormChange('sortOrder', val || 0)}
                  width="size-1600"
                />
                <TextField
                  label={`${translate('stripe_product_name')} *`}
                  value={tierForm.stripeProductName}
                  onChange={val => this.handleTierFormChange('stripeProductName', val)}
                  placeholder="e.g. AnyWARE Plus"
                  isDisabled={!!editingTier && !!editingTier.stripeProductId}
                  validationState={tierFormErrors.stripeProductName ? 'invalid' : undefined}
                  errorMessage={tierFormErrors.stripeProductName}
                  width="size-3000"
                />
                {editingTier && editingTier.stripeProductId && (
                  <TextField
                    label={translate('stripe_product_id_readonly')}
                    value={editingTier.stripeProductId}
                    isDisabled
                    width="size-3000"
                  />
                )}
              </Flex>
              <Checkbox isSelected={tierForm.isActive} onChange={val => this.handleTierFormChange('isActive', val)}>
                Active
              </Checkbox>
            </Flex>
            <Divider size="S" marginTop="size-200" marginBottom="size-150" />
            <Flex justifyContent="end">
              <ButtonGroup>
                <Button variant="cta" onPress={this.handleSaveTier}>{translate('save')}</Button>
                <Button variant="secondary" onPress={() => this.setState({ showTierForm: false })}>{translate('cancel')}</Button>
              </ButtonGroup>
            </Flex>
          </Well>
        )}

        {loading ? (
          <Flex justifyContent="center" marginY="size-400">
            <ProgressCircle aria-label={translate('loading_tiers')} size="L" isIndeterminate />
          </Flex>
        ) : (tiers && tiers.length > 0) ? (
          <TableView aria-label={translate('subscription_tiers')} overflowMode="wrap" density="spacious">
            <TableHeader>
              <Column key="tierKey" width={120}>{translate('tier_key_label')}</Column>
              <Column key="name">{translate('name')}</Column>
              <Column key="active" width={80}>{translate('active')}</Column>
              <Column key="stripe">{translate('stripe_product')}</Column>
              <Column key="features" width={100}>{translate('features_label')}</Column>
              <Column key="actions" width={220}>{translate('action')}</Column>
            </TableHeader>
            <TableBody>
              {(tiers || []).map(tier => (
                <Row key={tier.id}>
                  <Cell><span style={styles.code}>{tier.tierKey}</span></Cell>
                  <Cell>{tier.name}</Cell>
                  <Cell>{tier.isActive ? '✓' : '✗'}</Cell>
                  <Cell>
                    {tier.stripeProductId
                      ? <span style={styles.code}>{tier.stripeProductId}</span>
                      : <span style={{ color: '#999' }}>{translate('not_created')}</span>}
                  </Cell>
                  <Cell>
                    {(tier.features || []).filter(f => f.isEnabled).length} / {getFeatureDefinitions(this.props).length}
                  </Cell>
                  <Cell>
                    <Flex alignItems="center" gap="size-50">
                      <ActionGroup
                        density="compact"
                        orientation="horizontal"
                        onAction={(key) => {
                          switch (key) {
                            case 'features': this.handleOpenFeatures(tier); break
                            case 'pricing': this.handleOpenPricing(tier); break
                          }
                        }}
                      >
                        <Item key="features">{translate('features_label')}</Item>
                        <Item key="pricing">{translate('pricing_label')}</Item>
                      </ActionGroup>
                      <MenuTrigger>
                        <ActionButton aria-label="More actions">
                          <MoreVertical size={16} />
                        </ActionButton>
                        <Menu
                          onAction={(key) => {
                            switch (key) {
                              case 'edit': this.handleEditTier(tier); break
                              case 'delete': this.handleDeleteTier(tier.id); break
                            }
                          }}
                        >
                          <Item key="edit">{translate('edit_label')}</Item>
                          <Item key="delete">{translate('delete')}</Item>
                        </Menu>
                      </MenuTrigger>
                    </Flex>
                  </Cell>
                </Row>
              ))}
            </TableBody>
          </TableView>
        ) : (
          <View UNSAFE_style={{ textAlign: 'center', color: '#999', padding: '24px', fontStyle: 'italic' }}>
            <Text>{translate('no_tiers_configured')}</Text>
          </View>
        )}
      </View>
    )
  }

  // ── Render: Features Modal ──

  renderFeaturesModal() {
    const { featuresModalTier, featureForm } = this.state
    if (!featuresModalTier) return null

    const grouped: any = {}
    featureForm.forEach(f => {
      if (!grouped[f.category]) grouped[f.category] = []
      grouped[f.category].push(f)
    })

    return (
      <DialogTrigger isOpen onOpenChange={(isOpen) => { if (!isOpen) this.handleCloseFeatures() }}>
        <Button variant="primary" UNSAFE_style={{ display: 'none' }}>trigger</Button>
        {(close) => (
        <Provider theme={customSpectrumTheme} UNSAFE_className={SPECTRUM_THEME_CLASS} colorScheme="light">
        <Dialog size="L">
          <Heading>{this.props.translate('features_label')} — {featuresModalTier.name}</Heading>
          <Divider />
          <DialogContent UNSAFE_style={{ maxHeight: '60vh', overflow: 'auto' }}>
            {Object.entries(grouped).map(([category, features]: [string, any[]]) => (
              <View key={category} marginBottom="size-200">
                <Heading level={4} UNSAFE_style={{ fontSize: '14px', color: '#555', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  {category}
                </Heading>
                {features.map(f => (
                  <Checkbox
                    key={f.featureName}
                    isSelected={f.isEnabled}
                    onChange={checked => this.handleFeatureToggle(f.featureName, checked)}
                  >
                    {f.displayName}
                  </Checkbox>
                ))}
              </View>
            ))}
          </DialogContent>
          <ButtonGroup>
            <Button variant="cta" onPress={this.handleSaveFeatures} isDisabled={this.props.saving}>{this.props.translate('save_features')}</Button>
            <Button variant="secondary" onPress={this.handleCloseFeatures}>{this.props.translate('cancel')}</Button>
          </ButtonGroup>
        </Dialog>
        </Provider>
        )}
      </DialogTrigger>
    )
  }

  // ── Render: Pricing Modal ──

  renderPricingModal() {
    const { pricingModalTier, pricingForm, pricingLocked, showPricingConfirm, pricingConfirmTierName } = this.state
    if (!pricingModalTier) return null

    const grouped: any = {}
    pricingForm.forEach((p, i) => {
      if (!grouped[p.deviceCategory]) grouped[p.deviceCategory] = []
      grouped[p.deviceCategory].push({ ...p, _index: i })
    })

    const pricingChanges = this.getPricingDiff()
    const fieldLabelMap = {
      price1Device: this.props.translate('device_1_yearly'),
      price2To5: this.props.translate('device_2_5_yearly'),
      price6Plus: this.props.translate('device_6_plus_yearly')
    }

    // Confirmation dialog
    if (showPricingConfirm) {
      const tierName = pricingModalTier.name || ''
      const confirmValid = pricingConfirmTierName.trim().toLowerCase() === tierName.trim().toLowerCase()

      return (
        <DialogTrigger isOpen onOpenChange={(isOpen) => { if (!isOpen) this.setState({ showPricingConfirm: false }) }}>
          <Button variant="primary" UNSAFE_style={{ display: 'none' }}>trigger</Button>
          {(close) => (
          <Provider theme={customSpectrumTheme} UNSAFE_className={SPECTRUM_THEME_CLASS} colorScheme="light">
          <Dialog size="L" UNSAFE_style={{ maxWidth: '700px' }}>
            <Heading>{this.props.translate('confirm_price_change')}</Heading>
            <Divider />
            <DialogContent>
              <Well UNSAFE_style={{ backgroundColor: '#ffebee', borderColor: '#ef9a9a', marginBottom: '16px' }}>
                <Text UNSAFE_style={{ fontWeight: 600, color: '#c62828' }}>
                  {this.props.translate('pricing_change_warning')}
                </Text>
              </Well>

              <Heading level={4} marginBottom="size-100">{this.props.translate('pricing_changes_summary')}</Heading>
              <table style={styles.table}>
                <thead style={styles.thead}>
                  <tr>
                    <th style={styles.th}>{this.props.translate('device_category_label')}</th>
                    <th style={styles.th}>{this.props.translate('currency_label')}</th>
                    <th style={styles.th}>{this.props.translate('pricing_field')}</th>
                    <th style={styles.th}>{this.props.translate('old_price')}</th>
                    <th style={styles.th}>{this.props.translate('new_price')}</th>
                  </tr>
                </thead>
                <tbody>
                  {pricingChanges.map((change, idx) => (
                    <tr key={idx}>
                      <td style={styles.td}>{change.deviceCategory}</td>
                      <td style={styles.td}><strong>{change.currency}</strong></td>
                      <td style={styles.td}>{fieldLabelMap[change.field] || change.field}</td>
                      <td style={{ ...styles.td, color: '#c62828', textDecoration: 'line-through' }}>
                        {formatPrice(change.oldValue, change.currency)}
                      </td>
                      <td style={{ ...styles.td, color: '#2e7d32', fontWeight: 600 }}>
                        {formatPrice(change.newValue, change.currency)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <View marginTop="size-300">
                <Text UNSAFE_style={{ fontWeight: 600, marginBottom: '8px', display: 'block' }}>
                  {this.props.translate('pricing_confirm_type_tier_name').replace('{tierName}', tierName)}
                </Text>
                <TextField
                  label={this.props.translate('tier_name_label')}
                  value={pricingConfirmTierName}
                  onChange={val => this.setState({ pricingConfirmTierName: val })}
                  placeholder={tierName}
                  width="size-3000"
                  autoFocus
                />
              </View>
            </DialogContent>
            <ButtonGroup>
              <Button
                variant="negative"
                onPress={this.handleConfirmSavePricing}
                isDisabled={!confirmValid}
              >
                {this.props.translate('confirm_update_prices')}
              </Button>
              <Button variant="secondary" onPress={() => this.setState({ showPricingConfirm: false })}>
                {this.props.translate('cancel')}
              </Button>
            </ButtonGroup>
          </Dialog>
          </Provider>
          )}
        </DialogTrigger>
      )
    }

    return (
      <DialogTrigger isOpen onOpenChange={(isOpen) => { if (!isOpen) this.handleClosePricing() }}>
        <Button variant="primary" UNSAFE_style={{ display: 'none' }}>trigger</Button>
        {(close) => (
        <Provider theme={customSpectrumTheme} UNSAFE_className={SPECTRUM_THEME_CLASS} colorScheme="light">
        <Dialog size="L" UNSAFE_className="pricing-dialog-wide">
          <Heading>{this.props.translate('pricing_label')} — {pricingModalTier.name}</Heading>
          <Divider />
          <DialogContent UNSAFE_style={{ maxHeight: '60vh', overflow: 'auto' }}>
            {/* Lock/Unlock banner */}
            {pricingLocked ? (
              <Well UNSAFE_style={{ backgroundColor: '#e3f2fd', borderColor: '#90caf9', marginBottom: '16px' }}>
                <Flex alignItems="center" gap="size-200" justifyContent="space-between">
                  <Flex alignItems="center" gap="size-100">
                    <Lock size={18} color="#1565c0" />
                    <Text UNSAFE_style={{ color: '#1565c0', fontWeight: 600 }}>
                      {this.props.translate('pricing_locked_message')}
                    </Text>
                  </Flex>
                  <Button
                    variant="negative"
                    onPress={() => this.setState({ pricingLocked: false })}
                  >
                    <Unlock size={14} />
                    <Text>{this.props.translate('unlock_editing')}</Text>
                  </Button>
                </Flex>
              </Well>
            ) : (
              <Well UNSAFE_style={{ backgroundColor: '#fff3e0', borderColor: '#ffb74d', marginBottom: '16px' }}>
                <Flex alignItems="center" gap="size-100">
                  <Unlock size={18} color="#e65100" />
                  <Text UNSAFE_style={{ color: '#e65100', fontWeight: 600 }}>
                    {this.props.translate('pricing_unlocked_warning')}
                  </Text>
                </Flex>
              </Well>
            )}

            <Well UNSAFE_style={{ marginBottom: '16px' }}>
              <Text>{this.props.translate('pricing_hint')}</Text>
            </Well>

            {Object.entries(grouped).map(([category, rows]: [string, any[]]) => {
              const displayName = rows[0]?.deviceCategoryDisplay || category
              return (
                <View key={category} marginBottom="size-300">
                  <Heading level={4} UNSAFE_style={{ fontSize: '15px', color: '#333' }}>{displayName}</Heading>
                  <table style={styles.table}>
                    <thead style={styles.thead}>
                      <tr>
                        <th style={styles.th}>{this.props.translate('currency_label')}</th>
                        <th style={styles.th}>{this.props.translate('device_1_yearly')}</th>
                        <th style={styles.th}>{this.props.translate('device_2_5_yearly')}</th>
                        <th style={styles.th}>{this.props.translate('device_6_plus_yearly')}</th>
                        <th style={styles.th}>{this.props.translate('preview_label')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map(row => (
                        <tr key={row.currency}>
                          <td style={styles.td}>
                            <Text><strong>{row.currency}</strong> {row.currencySymbol}</Text>
                          </td>
                          <td style={styles.td}>
                            <input type="number" style={{ ...styles.inputSm, ...(pricingLocked ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}) }} value={row.price1Device} disabled={pricingLocked} onChange={e => this.handlePricingChange(row._index, 'price1Device', e.target.value)} />
                          </td>
                          <td style={styles.td}>
                            <input type="number" style={{ ...styles.inputSm, ...(pricingLocked ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}) }} value={row.price2To5} disabled={pricingLocked} onChange={e => this.handlePricingChange(row._index, 'price2To5', e.target.value)} />
                          </td>
                          <td style={styles.td}>
                            <input type="number" style={{ ...styles.inputSm, ...(pricingLocked ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}) }} value={row.price6Plus} disabled={pricingLocked} onChange={e => this.handlePricingChange(row._index, 'price6Plus', e.target.value)} />
                          </td>
                          <td style={{ ...styles.td, color: '#888', fontSize: '12px' }}>
                            <Text UNSAFE_style={{ color: '#888', fontSize: '12px' }}>
                              {formatPrice(row.price1Device, row.currency)} / {formatPrice(row.price2To5, row.currency)} / {formatPrice(row.price6Plus, row.currency)}
                            </Text>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </View>
              )
            })}
          </DialogContent>
          <ButtonGroup>
            {!pricingLocked && (
              <Button variant="cta" onPress={this.handleSavePricing}>{this.props.translate('save_pricing')}</Button>
            )}
            <Button variant="secondary" onPress={this.handleClosePricing}>{pricingLocked ? this.props.translate('close') : this.props.translate('cancel')}</Button>
          </ButtonGroup>
        </Dialog>
        </Provider>
        )}
      </DialogTrigger>
    )
  }

  // ── Render: Countries ──

  renderCountriesView() {
    const { countries, loading, translate } = this.props
    const { showCountryForm, editingCountry, countryForm, editingCountryCurrencies, currencyForm } = this.state

    return (
      <View>
        <Flex justifyContent="space-between" alignItems="center" marginBottom="size-200">
          <Heading level={3} margin="size-0">{translate('checkout_countries')}</Heading>
          <Button variant="cta" onPress={this.handleNewCountry}>{translate('new_country')}</Button>
        </Flex>

        {showCountryForm && (
          <Well marginBottom="size-200">
            <Heading level={4} marginBottom="size-100">{editingCountry ? translate('edit_country') : translate('new_country')}</Heading>
            <Flex gap="size-200" wrap>
              <TextField
                label={translate('country_code_label')}
                value={countryForm.countryCode}
                onChange={val => this.handleCountryFormChange('countryCode', val.toUpperCase())}
                maxLength={2}
                placeholder="e.g. GB"
                width="size-1600"
              />
              <TextField
                label={translate('country_name_label')}
                value={countryForm.countryName}
                onChange={val => this.handleCountryFormChange('countryName', val)}
                placeholder="e.g. United Kingdom"
                width="size-3000"
              />
              <NumberField
                label={translate('sort_order')}
                value={countryForm.sortOrder}
                onChange={val => this.handleCountryFormChange('sortOrder', val || 0)}
                width="size-1600"
              />
              <Picker
                label={translate('stripe_seller_label')}
                description={translate('stripe_seller_description')}
                selectedKey={countryForm.stripeSellerKey || 'none'}
                onSelectionChange={key => this.handleCountryFormChange('stripeSellerKey', key === 'none' ? '' : String(key))}
                width="size-3000"
              >
                <Item key="none">{translate('stripe_seller_none')}</Item>
                <Item key="uk">UK</Item>
                <Item key="eu">EU</Item>
                <Item key="us">US</Item>
              </Picker>
            </Flex>
            <Checkbox isSelected={countryForm.isActive} onChange={val => this.handleCountryFormChange('isActive', val)} marginTop="size-100">
              {translate('active')}
            </Checkbox>
            <Divider size="S" marginTop="size-200" marginBottom="size-150" />
            <Flex justifyContent="end">
              <ButtonGroup>
                <Button variant="cta" onPress={this.handleSaveCountry}>{translate('save')}</Button>
                <Button variant="secondary" onPress={() => this.setState({ showCountryForm: false })}>{translate('cancel')}</Button>
              </ButtonGroup>
            </Flex>
          </Well>
        )}

        {editingCountryCurrencies && (
          <Well marginBottom="size-200">
            <Heading level={4} marginBottom="size-100">{translate('currencies_for')} {editingCountryCurrencies.countryName} ({editingCountryCurrencies.countryCode})</Heading>
            <table style={styles.table}>
              <thead style={styles.thead}>
                <tr>
                  <th style={styles.th}>{translate('currency_label')}</th>
                  <th style={styles.th}>{translate('default_label')}</th>
                  <th style={styles.th}>{translate('active')}</th>
                  <th style={styles.th}>{translate('action')}</th>
                </tr>
              </thead>
              <tbody>
                {currencyForm.map((curr, idx) => (
                  <tr key={idx}>
                    <td style={styles.td}>
                      <Picker
                        selectedKey={curr.currencyCode}
                        onSelectionChange={val => this.handleCurrencyChange(idx, 'currencyCode', val)}
                        placeholder={translate('select_currency')}
                        width="size-6000"
                      >
                        {getSupportedCurrencies(this.props).map(sc => (
                          <Item key={sc.code} textValue={`${sc.code} (${sc.symbol}) — ${sc.name}`}>{`${sc.code} (${sc.symbol}) — ${sc.name}`}</Item>
                        ))}
                      </Picker>
                    </td>
                    <td style={styles.td}><input type="radio" name="defaultCurrency" checked={curr.isDefault} onChange={() => this.handleCurrencyChange(idx, 'isDefault', true)} /></td>
                    <td style={styles.td}><Checkbox isSelected={curr.isActive} onChange={val => this.handleCurrencyChange(idx, 'isActive', val)} aria-label={translate('active')} /></td>
                    <td style={styles.td}><Button variant="negative" isQuiet onPress={() => this.handleRemoveCurrency(idx)}>{translate('remove_label')}</Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Divider size="S" marginTop="size-200" marginBottom="size-150" />
            <Flex justifyContent="end">
              <ButtonGroup>
                <Button variant="secondary" onPress={this.handleAddCurrency}>{translate('add_currency')}</Button>
                <Button variant="cta" onPress={this.handleSaveCurrencies}>{translate('save_currencies')}</Button>
                <Button variant="secondary" onPress={() => this.setState({ editingCountryCurrencies: null })}>{translate('cancel')}</Button>
              </ButtonGroup>
            </Flex>
          </Well>
        )}

        {loading ? (
          <Flex justifyContent="center" marginY="size-400">
            <ProgressCircle aria-label={translate('loading_countries')} size="L" isIndeterminate />
          </Flex>
        ) : (countries && countries.length > 0) ? (
          <TableView aria-label={translate('checkout_countries')} overflowMode="wrap" density="spacious">
            <TableHeader>
              <Column key="code" width={80}>{translate('code_label')}</Column>
              <Column key="country">{translate('country_label')}</Column>
              <Column key="active" width={80}>{translate('active')}</Column>
              <Column key="sort" width={80}>{translate('sort_label')}</Column>
              <Column key="seller" width={100}>{translate('stripe_seller_label')}</Column>
              <Column key="currencies">{translate('currency_label')}</Column>
              <Column key="actions" width={180}>{translate('action')}</Column>
            </TableHeader>
            <TableBody>
              {(countries || []).map(country => (
                <Row key={country.id}>
                  <Cell><span style={styles.code}>{country.countryCode}</span></Cell>
                  <Cell>{country.countryName}</Cell>
                  <Cell>{country.isActive ? '✓' : '✗'}</Cell>
                  <Cell>{country.sortOrder}</Cell>
                  <Cell>{country.stripeSellerKey ? country.stripeSellerKey.toUpperCase() : '—'}</Cell>
                  <Cell>
                    {(country.currencies || []).map(c => (
                      <span key={c.currencyCode} style={c.isDefault ? styles.currencyBadgeDefault : styles.currencyBadge}>
                        {c.currencyCode} {c.currencySymbol}
                      </span>
                    ))}
                  </Cell>
                  <Cell>
                    <Flex alignItems="center" gap="size-50">
                      <ActionGroup
                        density="compact"
                        orientation="horizontal"
                        onAction={(key) => {
                          if (key === 'currencies') this.handleEditCurrencies(country)
                        }}
                      >
                        <Item key="currencies">{translate('currency_label')}</Item>
                      </ActionGroup>
                      <MenuTrigger>
                        <ActionButton aria-label="More actions">
                          <MoreVertical size={16} />
                        </ActionButton>
                        <Menu
                          onAction={(key) => {
                            switch (key) {
                              case 'edit': this.handleEditCountry(country); break
                              case 'delete': this.handleDeleteCountry(country.id); break
                            }
                          }}
                        >
                          <Item key="edit">{translate('edit_label')}</Item>
                          <Item key="delete">{translate('delete')}</Item>
                        </Menu>
                      </MenuTrigger>
                    </Flex>
                  </Cell>
                </Row>
              ))}
            </TableBody>
          </TableView>
        ) : (
          <View UNSAFE_style={{ textAlign: 'center', color: '#999', padding: '24px', fontStyle: 'italic' }}>
            <Text>{translate('no_countries_configured')}</Text>
          </View>
        )}
      </View>
    )
  }

  // ── Render: Terms & Conditions ──

  execRichCommand = (command, value = null) => {
    const iframe = this._editorIframe
    if (iframe && iframe.contentDocument) {
      iframe.contentDocument.execCommand(command, false, value)
      // Sync content back to state
      const html = iframe.contentDocument.body.innerHTML
      this.handleTermsFormChange('content', html)
    }
  }

  handleEditorLoad = () => {
    const iframe = this._editorIframe
    if (iframe && iframe.contentDocument) {
      const doc = iframe.contentDocument
      doc.designMode = 'on'
      doc.open()
      doc.write(`<!DOCTYPE html><html><head><style>
        body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; font-size: 14px; color: #333; line-height: 1.6; padding: 12px; margin: 0; }
        body:focus { outline: none; }
        h1, h2, h3 { color: #005795; }
        a { color: #1976d2; }
        table { border-collapse: collapse; width: 100%; }
        td, th { border: 1px solid #ddd; padding: 8px; }
      </style></head><body>${this.state.termsForm.content || ''}</body></html>`)
      doc.close()
      doc.body.contentEditable = 'true'
      // Sync changes on input
      doc.addEventListener('input', () => {
        const html = doc.body.innerHTML
        this.handleTermsFormChange('content', html)
      })
    }
  }

  syncEditorContent = () => {
    const iframe = this._editorIframe
    if (iframe && iframe.contentDocument && iframe.contentDocument.body) {
      iframe.contentDocument.body.innerHTML = this.state.termsForm.content || ''
    }
  }

  renderTermsView() {
    const { termsVersions, loading, translate } = this.props
    const { showTermsForm, editingTerms, termsForm, termsFormErrors, termsContentLoading } = this.state

    const SUPPORTED_LANGUAGES = [
      { code: 'en-GB', name: 'English (UK)' },
      { code: 'en-US', name: 'English (US)' },
      { code: 'de-DE', name: 'German' },
      { code: 'fr-FR', name: 'French' },
      { code: 'it-IT', name: 'Italian' },
      { code: 'es-ES', name: 'Spanish' },
      { code: 'pt-PT', name: 'Portuguese' },
      { code: 'pl-PL', name: 'Polish' },
      { code: 'ja-JP', name: 'Japanese' },
      { code: 'zh-CN', name: 'Chinese (Simplified)' },
      { code: 'ru-RU', name: 'Russian' }
    ]

    const toolbarBtnStyle = {
      padding: '4px 8px',
      border: '1px solid #ccc',
      borderRadius: '3px',
      backgroundColor: '#fff',
      cursor: 'pointer',
      fontSize: '13px',
      fontWeight: 600,
      minWidth: '28px',
      lineHeight: '18px'
    }

    const toolbarSeparator = {
      display: 'inline-block',
      width: '1px',
      height: '20px',
      backgroundColor: '#ddd',
      margin: '0 6px',
      verticalAlign: 'middle'
    }

    return (
      <View>
        <Flex justifyContent="space-between" alignItems="center" marginBottom="size-200">
          <Heading level={3} margin="size-0">{translate('terms_and_conditions')}</Heading>
          <Button variant="cta" onPress={this.handleNewTerms}>{translate('new_terms_version')}</Button>
        </Flex>

        <Well marginBottom="size-200">
          <Text>{translate('terms_instructions')}</Text>
        </Well>

        {/* Missing languages warning */}
        {(() => {
          const publishedLangs = new Set(
            (termsVersions || [])
              .filter(t => t.status === 'Published')
              .map(t => t.languageCode)
          )
          const missing = SUPPORTED_LANGUAGES.filter(l => !publishedLangs.has(l.code))
          if (missing.length > 0) {
            return (
              <Well marginBottom="size-200" UNSAFE_style={{ backgroundColor: '#fff3e0', borderColor: '#ffb74d' }}>
                <Text UNSAFE_style={{ fontWeight: 600, color: '#e65100' }}>
                  {translate('terms_missing_languages_warning')}
                </Text>
                <Text UNSAFE_style={{ color: '#bf360c', marginTop: '4px', display: 'block' }}>
                  {missing.map(l => `${l.name} (${l.code})`).join(', ')}
                </Text>
              </Well>
            )
          }
          return null
        })()}

        {showTermsForm && (
          <Well marginBottom="size-200">
            <Flex alignItems="center" gap="size-100" marginBottom="size-100">
              <Heading level={4} margin="size-0">
                {editingTerms ? translate('edit_terms_version') : translate('new_terms_version_label')}
              </Heading>
              {editingTerms && editingTerms.status === 'Published' && (
                <span style={{ fontSize: '12px', color: '#2e7d32', backgroundColor: '#e8f5e9', padding: '2px 8px', borderRadius: '12px' }}>
                  {translate('terms_published_readonly')}
                </span>
              )}
            </Flex>
            <Flex gap="size-200" wrap>
              <Picker
                label={translate('terms_language_label')}
                selectedKey={termsForm.languageCode}
                onSelectionChange={val => this.handleTermsFormChange('languageCode', val)}
                isDisabled={!!editingTerms}
                validationState={termsFormErrors.languageCode ? 'invalid' : undefined}
                width="size-3000"
              >
                {SUPPORTED_LANGUAGES.map(l => (
                  <Item key={l.code} textValue={`${l.name} (${l.code})`}>{`${l.name} (${l.code})`}</Item>
                ))}
              </Picker>
              <TextField
                label={translate('terms_version_label')}
                value={termsForm.version}
                onChange={val => this.handleTermsFormChange('version', val)}
                placeholder="e.g. 1.0, 1.1, 2.0"
                isReadOnly={editingTerms && editingTerms.status === 'Published'}
                validationState={termsFormErrors.version ? 'invalid' : undefined}
                errorMessage={termsFormErrors.version}
                width="size-2000"
              />
              <TextField
                label={translate('terms_title_label')}
                value={termsForm.title}
                onChange={val => this.handleTermsFormChange('title', val)}
                placeholder="e.g. Terms and Conditions"
                isReadOnly={editingTerms && editingTerms.status === 'Published'}
                validationState={termsFormErrors.title ? 'invalid' : undefined}
                errorMessage={termsFormErrors.title}
                width="100%"
              />
            </Flex>

            {/* Rich Text Editor — keeping toolbar as native buttons for rich-text commands */}
            <View marginTop="size-200">
              <Text UNSAFE_style={{ fontWeight: 600, fontSize: '14px', marginBottom: '8px', display: 'block' }}>{translate('terms_content_label')}</Text>

              {termsContentLoading ? (
                <Flex justifyContent="center" alignItems="center" UNSAFE_style={{ padding: '40px', border: '1px solid #ddd', borderRadius: '4px' }}>
                  <ProgressCircle aria-label={translate('loading_content')} size="M" isIndeterminate />
                  <Text marginStart="size-100">{translate('loading_content')}</Text>
                </Flex>
              ) : (
                <>
                  {/* Toolbar — kept as native buttons for execCommand integration */}
                  {!(editingTerms && editingTerms.status === 'Published') && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', padding: '8px', backgroundColor: '#f8f8f8', border: '1px solid #ddd', borderBottom: 'none', borderRadius: '4px 4px 0 0', alignItems: 'center' }}>
                      <button type="button" style={toolbarBtnStyle} onClick={() => this.execRichCommand('bold')} title="Bold"><b>B</b></button>
                      <button type="button" style={toolbarBtnStyle} onClick={() => this.execRichCommand('italic')} title="Italic"><i>I</i></button>
                      <button type="button" style={toolbarBtnStyle} onClick={() => this.execRichCommand('underline')} title="Underline"><u>U</u></button>
                      <button type="button" style={toolbarBtnStyle} onClick={() => this.execRichCommand('strikeThrough')} title="Strikethrough"><s>S</s></button>
                      <span style={toolbarSeparator} />
                      <button type="button" style={toolbarBtnStyle} onClick={() => this.execRichCommand('formatBlock', '<h1>')} title="Heading 1">H1</button>
                      <button type="button" style={toolbarBtnStyle} onClick={() => this.execRichCommand('formatBlock', '<h2>')} title="Heading 2">H2</button>
                      <button type="button" style={toolbarBtnStyle} onClick={() => this.execRichCommand('formatBlock', '<h3>')} title="Heading 3">H3</button>
                      <button type="button" style={toolbarBtnStyle} onClick={() => this.execRichCommand('formatBlock', '<p>')} title="Paragraph">¶</button>
                      <span style={toolbarSeparator} />
                      <button type="button" style={toolbarBtnStyle} onClick={() => this.execRichCommand('insertUnorderedList')} title="Bullet List">• List</button>
                      <button type="button" style={toolbarBtnStyle} onClick={() => this.execRichCommand('insertOrderedList')} title="Numbered List">1. List</button>
                      <span style={toolbarSeparator} />
                      <button type="button" style={toolbarBtnStyle} onClick={() => this.execRichCommand('justifyLeft')} title="Align Left">⫷</button>
                      <button type="button" style={toolbarBtnStyle} onClick={() => this.execRichCommand('justifyCenter')} title="Align Center">⫶</button>
                      <button type="button" style={toolbarBtnStyle} onClick={() => this.execRichCommand('justifyRight')} title="Align Right">⫸</button>
                      <span style={toolbarSeparator} />
                      <button type="button" style={toolbarBtnStyle} onClick={() => {
                        const url = prompt(translate('enter_link_url'))
                        if (url) this.execRichCommand('createLink', url)
                      }} title="Insert Link">🔗 Link</button>
                      <button type="button" style={toolbarBtnStyle} onClick={() => this.execRichCommand('unlink')} title="Remove Link">Unlink</button>
                      <button type="button" style={toolbarBtnStyle} onClick={() => this.execRichCommand('insertHorizontalRule')} title="Horizontal Rule">—</button>
                      <span style={toolbarSeparator} />
                      <button type="button" style={toolbarBtnStyle} onClick={() => this.execRichCommand('removeFormat')} title="Clear Formatting">✕ Clear</button>
                      <button type="button" style={{ ...toolbarBtnStyle, marginLeft: 'auto', fontSize: '12px', color: '#666' }} onClick={() => {
                        this.setState(prev => ({ showHtmlSource: !prev.showHtmlSource }))
                      }} title="Toggle HTML source">
                        {this.state.showHtmlSource ? '📝 Visual' : '</> HTML'}
                      </button>
                    </div>
                  )}

                  {/* Editor area */}
                  {this.state.showHtmlSource ? (
                    <textarea
                      style={{
                        width: '100%',
                        minHeight: '400px',
                        padding: '12px',
                        border: '1px solid #ddd',
                        borderRadius: editingTerms && editingTerms.status === 'Published' ? '4px' : '0 0 4px 4px',
                        fontSize: '13px',
                        fontFamily: 'monospace',
                        lineHeight: '1.5',
                        resize: 'vertical',
                        outline: 'none',
                        backgroundColor: editingTerms && editingTerms.status === 'Published' ? '#f5f5f5' : '#fff',
                        boxSizing: 'border-box'
                      }}
                      value={termsForm.content}
                      onChange={e => {
                        this.handleTermsFormChange('content', e.target.value)
                      }}
                      readOnly={editingTerms && editingTerms.status === 'Published'}
                      placeholder="Enter HTML content for the Terms & Conditions..."
                    />
                  ) : (
                    <iframe
                      ref={el => {
                        if (el && el !== this._editorIframe) {
                          this._editorIframe = el
                          setTimeout(() => this.handleEditorLoad(), 50)
                        }
                      }}
                      style={{
                        width: '100%',
                        minHeight: '400px',
                        border: '1px solid #ddd',
                        borderRadius: editingTerms && editingTerms.status === 'Published' ? '4px' : '0 0 4px 4px',
                        backgroundColor: '#fff',
                        boxSizing: 'border-box'
                      }}
                      title="Terms content editor"
                    />
                  )}
                  {termsFormErrors.content && <span style={styles.fieldError}>{termsFormErrors.content}</span>}
                </>
              )}
            </View>

            <Divider size="S" marginTop="size-200" marginBottom="size-150" />
            <Flex justifyContent="end">
              <ButtonGroup>
                {!(editingTerms && editingTerms.status === 'Published') && (
                  <Button variant="cta" onPress={this.handleSaveTerms}>
                    {editingTerms ? translate('update') : translate('save_draft')}
                  </Button>
                )}
                <Button variant="secondary" onPress={() => this.setState({ showTermsForm: false, showHtmlSource: false })}>
                  {editingTerms && editingTerms.status === 'Published' ? translate('close') : translate('cancel')}
                </Button>
                {termsForm.content && (
                  <Button variant="secondary" onPress={() => this.handlePreviewTerms({ ...termsForm, status: editingTerms ? editingTerms.status : 'Draft' })}>
                    {translate('preview_in_new_tab')}
                  </Button>
                )}
              </ButtonGroup>
            </Flex>
          </Well>
        )}

        {loading ? (
          <Flex justifyContent="center" marginY="size-400">
            <ProgressCircle aria-label="Loading" size="L" isIndeterminate />
          </Flex>
        ) : (termsVersions && termsVersions.length > 0) ? (
          <TableView aria-label={translate('terms_and_conditions')} overflowMode="wrap" density="spacious">
            <TableHeader>
              <Column key="language" width={80}>{translate('language_header')}</Column>
              <Column key="version" width={80}>{translate('version_header')}</Column>
              <Column key="title">{translate('title_header')}</Column>
              <Column key="status" width={100}>{translate('status')}</Column>
              <Column key="published" width={110}>{translate('published_header')}</Column>
              <Column key="modified" width={110}>{translate('last_modified_header')}</Column>
              <Column key="actions" width={200}>{translate('actions_header')}</Column>
            </TableHeader>
            <TableBody>
              {(termsVersions || []).map(terms => (
                <Row key={getTermsId(terms)}>
                  <Cell><span style={styles.code}>{terms.languageCode}</span></Cell>
                  <Cell>{terms.version}</Cell>
                  <Cell>{terms.title}</Cell>
                  <Cell>
                    <span style={{
                      display: 'inline-block',
                      padding: '2px 10px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: 600,
                      backgroundColor: terms.status === 'Published' ? '#e8f5e9' : '#fff3e0',
                      color: terms.status === 'Published' ? '#2e7d32' : '#e65100'
                    }}>
                      {terms.status}
                    </span>
                  </Cell>
                  <Cell>
                    {terms.publishedAt
                      ? new Date(terms.publishedAt).toLocaleDateString()
                      : <span style={{ color: '#999' }}>—</span>}
                  </Cell>
                  <Cell>
                    {terms.modifiedAt
                      ? new Date(terms.modifiedAt).toLocaleDateString()
                      : terms.createdAt
                        ? new Date(terms.createdAt).toLocaleDateString()
                        : '—'}
                  </Cell>
                  <Cell>
                    <Flex alignItems="center" gap="size-50">
                      <ActionGroup
                        density="compact"
                        orientation="horizontal"
                        onAction={(key) => {
                          switch (key) {
                            case 'preview': this.handlePreviewTerms(terms); break
                            case 'edit': this.handleEditTerms(terms); break
                          }
                        }}
                      >
                        <Item key="preview">{translate('preview')}</Item>
                        <Item key="edit">{terms.status === 'Published' ? translate('view') : translate('edit_label')}</Item>
                      </ActionGroup>
                      <MenuTrigger>
                        <ActionButton aria-label="More actions">
                          <MoreVertical size={16} />
                        </ActionButton>
                        <Menu
                          disabledKeys={terms.status !== 'Draft' ? ['publish'] : []}
                          onAction={(key) => {
                            switch (key) {
                              case 'publish': this.handlePublishTerms(getTermsId(terms)); break
                              case 'duplicate': this.handleDuplicateTerms(terms); break
                              case 'delete': this.handleDeleteTerms(getTermsId(terms)); break
                            }
                          }}
                        >
                          <Item key="publish">{translate('publish')}</Item>
                          <Item key="duplicate">{translate('duplicate')}</Item>
                          <Item key="delete">{translate('delete')}</Item>
                        </Menu>
                      </MenuTrigger>
                    </Flex>
                  </Cell>
                </Row>
              ))}
            </TableBody>
          </TableView>
          ) : (
          <View UNSAFE_style={{ textAlign: 'center', color: '#999', padding: '24px', fontStyle: 'italic' }}>
            <Text>{translate('no_terms_configured')}</Text>
          </View>
        )}
      </View>
    )
  }

  // ── Main Render ──

  render() {
    const { translate } = this.props
    const { mainTab } = this.state

    return (
      <Provider theme={customSpectrumTheme} UNSAFE_className={SPECTRUM_THEME_CLASS} colorScheme="light">
        <Content translate={translate}>
          <View UNSAFE_style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px', paddingTop: '72px' }}>
            <Flex direction="column" gap="size-200">
              {/* Header */}
              <View marginBottom="size-200">
                <Heading level={1} UNSAFE_style={{ margin: '0 0 8px 0' }}>{translate('subscription_management')}</Heading>
                <Text UNSAFE_style={{ color: '#666', fontSize: '14px' }}>{translate('configure_tiers_desc')}</Text>
              </View>

              {this.renderMessages()}

              {/* Tabs */}
              <Tabs
                selectedKey={mainTab}
                onSelectionChange={(key) => this.setState({ mainTab: key })}
              >
                <TabList>
                  <Item key="tiers">{translate('subscription_tiers')}</Item>
                  <Item key="countries">{translate('countries_currencies')}</Item>
                  <Item key="terms">{translate('terms_and_conditions')}</Item>
                </TabList>
                <TabPanels>
                  <Item key="tiers">
                    <View padding="size-200">
                      {this.renderTiersView()}
                    </View>
                  </Item>
                  <Item key="countries">
                    <View padding="size-200">
                      {this.renderCountriesView()}
                    </View>
                  </Item>
                  <Item key="terms">
                    <View padding="size-200">
                      {this.renderTermsView()}
                    </View>
                  </Item>
                </TabPanels>
              </Tabs>
            </Flex>

            {this.renderFeaturesModal()}
            {this.renderPricingModal()}
          </View>
        </Content>
      </Provider>
    )
  }
}

export default SubscriptionManagement
